// src/utils/sampleDataGenerator.ts
import { assetStore, assetActions } from '../stores/assetStore';
import { riskStore, riskActions } from '../stores/riskStore';
import { nonInfoSecRiskStore, nonInfoSecRiskActions } from '../stores/nonInfoSecRiskStore';
import { adminStore } from '../stores/adminStore';
import { globalConfigStore } from '../stores/globalConfigStore';
import { riskMatrixActions } from '../stores/riskMatrixStore';
import { api } from './api';
import { getAssetDisplayName } from './assetDisplayName';

const generateId = () => `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const generateRefNumber = () => '';  // Server generates reference numbers atomically

// ISO 27001 Controls mapped by common risk scenarios
const controlsByCategory = {
  'Human Resources': [
    '6.1 Screening',
    '6.2 Terms and conditions of employment',
    '6.3 Information security awareness, education and training',
    '6.4 Disciplinary process',
    '6.5 Responsibilities after termination or change of employment'
  ],
  'Infrastructure': [
    '7.1 Physical security perimeters',
    '7.2 Physical entry',
    '7.4 Physical security monitoring',
    '7.8 Equipment siting and protection',
    '7.11 Supporting utilities',
    '7.13 Equipment maintenance'
  ],
  'IT and communication equipment': [
    '8.1 User endpoint devices',
    '8.7 Protection against malware',
    '8.8 Management of technical vulnerabilities',
    '8.10 Information deletion',
    '8.11 Data masking',
    '8.15 Logging',
    '8.24 Use of cryptography'
  ],
  'Network': [
    '8.20 Networks security',
    '8.21 Security of network services',
    '8.22 Segregation of networks',
    '8.23 Web filtering'
  ],
  'Other document and data (in electronic and other forms)': [
    '5.10 Acceptable use of information and other associated assets',
    '5.12 Classification of information',
    '5.13 Labelling of information',
    '5.14 Information transfer',
    '5.33 Protection of records',
    '8.11 Data masking'
  ],
  'Software and databases': [
    '8.9 Configuration management',
    '8.19 Installation of software on operational systems',
    '8.25 Secure development life cycle',
    '8.26 Application security requirements',
    '8.28 Secure coding',
    '8.32 Change management'
  ],
  'Third party service': [
    '5.19 Information security in supplier relationships',
    '5.20 Addressing information security within supplier agreements',
    '5.21 Managing information security in the ICT supply chain',
    '5.22 Monitoring, review and change management of supplier services',
    '5.23 Information security for use of cloud services'
  ]
};

export interface GenerationResult {
  assetsGenerated: number;
  risksGenerated: number;
  businessRisksGenerated: number;
  errors: string[];
}

export const generateAllSampleData = async (): Promise<GenerationResult> => {
  const errors: string[] = [];

  // Delete existing data from database
  try {
    await Promise.all([
      api.del('/assets'),
      api.del('/risks'),
      api.del('/non-infosec-risks'),
    ]);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Unknown error';
    throw new Error(`Failed to clear existing data: ${msg}`);
  }

  // Clear local stores
  assetStore.assets = [];
  riskStore.risks = [];
  nonInfoSecRiskStore.risks = [];

  // Reset filters
  riskStore.searchTerm = '';
  riskStore.categoryFilter = 'all';
  riskStore.statusFilter = 'all';
  nonInfoSecRiskStore.searchTerm = '';
  nonInfoSecRiskStore.categoryFilter = 'all';
  nonInfoSecRiskStore.statusFilter = 'all';
  nonInfoSecRiskStore.treatmentFilter = 'all';

  // Get dynamic data from global config and admin store
  const treatmentStrategies = globalConfigStore.treatmentStrategies.map(t => t.name);
  const riskCategories = globalConfigStore.riskCategories.map(r => r.name);
  const owners = adminStore.owners.map(o => o.name);
  const locations = adminStore.locations.map(l => l.name);

  // Fallback if admin store is empty
  const treatments = treatmentStrategies.length > 0 ? treatmentStrategies : ['Treat', 'Tolerate', 'Transfer', 'Terminate'];
  const categories = riskCategories.length > 0 ? riskCategories : ['Confidentiality', 'Integrity', 'Availability'];
  const ownerList = owners.length > 0 ? owners : ['IT Department', 'HR Department', 'Finance', 'Security', 'Operations', 'Facilities'];
  const locationList = locations.length > 0 ? locations : ['Server Room', 'Office Floor 1', 'Office Floor 2', 'Cloud - Azure', 'Cloud - AWS', 'SharePoint', 'Building Entrances'];

  const iso27001Categories = [
    'Human Resources',
    'Infrastructure',
    'IT and communication equipment',
    'Network',
    'Other document and data (in electronic and other forms)',
    'Software and databases',
    'Third party service'
  ];

  const now = new Date();
  const getDate = (daysOffset: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() + daysOffset);
    return d.toISOString().split('T')[0];
  };

  // Generate 30 assets - distributed across all ISO categories
  const assetTemplates = [
    // Human Resources (4 assets)
    { make: '', model: 'Employee Records Database', assetGroup: 'HR Systems', iso: 'Human Resources', location: 'Server Room', owner: ownerList[1] || 'HR Department', cost: '8000' },
    { make: '', model: 'Contractor Management System', assetGroup: 'HR Systems', iso: 'Human Resources', location: 'Cloud - Azure', owner: ownerList[1] || 'HR Department', cost: '5000' },
    { make: '', model: 'Training Records', assetGroup: 'Document Stores', iso: 'Human Resources', location: 'SharePoint', owner: ownerList[1] || 'HR Department', cost: '0' },
    { make: '', model: 'Recruitment Portal', assetGroup: 'HR Systems', iso: 'Human Resources', location: 'Cloud - AWS', owner: ownerList[1] || 'HR Department', cost: '3000' },

    // Infrastructure (5 assets)
    { make: '', model: 'Main Office Building', assetGroup: 'Buildings', iso: 'Infrastructure', location: locationList[0] || 'Main Building', owner: ownerList[5] || 'Facilities', cost: '2500000' },
    { make: '', model: 'Server Room HVAC System', assetGroup: 'Environmental Controls', iso: 'Infrastructure', location: locationList[0] || 'Server Room', owner: ownerList[5] || 'Facilities', cost: '45000' },
    { make: 'APC', model: 'Smart-UPS 3000', assetGroup: 'Power Systems', iso: 'Infrastructure', location: locationList[0] || 'Server Room', owner: ownerList[0] || 'IT Department', cost: '12000' },
    { make: '', model: 'Physical Access Control System', assetGroup: 'Physical Security', iso: 'Infrastructure', location: 'All Building Entrances', owner: ownerList[3] || 'Security', cost: '35000' },
    { make: '', model: 'CCTV Surveillance System', assetGroup: 'Physical Security', iso: 'Infrastructure', location: 'Perimeter & Internal', owner: ownerList[3] || 'Security', cost: '18000' },

    // IT and communication equipment (8 assets)
    { make: 'Dell', model: 'PowerEdge R740 Server', assetGroup: 'Servers', iso: 'IT and communication equipment', location: locationList[0] || 'Server Room', owner: ownerList[0] || 'IT Department', cost: '8500' },
    { make: 'Dell', model: 'Latitude 5520 Laptop Fleet', assetGroup: 'Laptops', iso: 'IT and communication equipment', location: 'Various Offices', owner: ownerList[0] || 'IT Department', cost: '65000' },
    { make: 'HP', model: 'EliteDesk 800 Workstations', assetGroup: 'Desktops', iso: 'IT and communication equipment', location: 'Office Floor 1-3', owner: ownerList[0] || 'IT Department', cost: '42000' },
    { make: 'Apple', model: 'iPhone 13 Pro', assetGroup: 'Mobile Devices', iso: 'IT and communication equipment', location: 'Issued to Staff', owner: ownerList[0] || 'IT Department', cost: '28000' },
    { make: 'Dell', model: 'PowerEdge R640 Backup Server', assetGroup: 'Servers', iso: 'IT and communication equipment', location: locationList[0] || 'Server Room', owner: ownerList[0] || 'IT Department', cost: '7200' },
    { make: 'Synology', model: 'DS920+ NAS', assetGroup: 'Storage', iso: 'IT and communication equipment', location: locationList[0] || 'Server Room', owner: ownerList[0] || 'IT Department', cost: '3500' },
    { make: 'Zebra', model: 'ZT230 Label Printer', assetGroup: 'Printers', iso: 'IT and communication equipment', location: 'Warehouse', owner: ownerList[4] || 'Operations', cost: '4800' },
    { make: 'Samsung', model: '27" Monitor', assetGroup: 'Monitors', iso: 'IT and communication equipment', location: 'All Workstations', owner: ownerList[0] || 'IT Department', cost: '12000' },

    // Network (4 assets)
    { make: 'Cisco', model: 'Catalyst 2960-X Switch', assetGroup: 'Network Equipment', iso: 'Network', location: locationList[0] || 'Server Room', owner: ownerList[0] || 'IT Department', cost: '4500' },
    { make: 'Fortinet', model: 'FortiGate 100F Firewall', assetGroup: 'Network Equipment', iso: 'Network', location: locationList[0] || 'Server Room', owner: ownerList[3] || 'IT Security', cost: '5800' },
    { make: 'Cisco', model: 'Meraki MR46 Access Points', assetGroup: 'Network Equipment', iso: 'Network', location: 'All Floors', owner: ownerList[0] || 'IT Department', cost: '8400' },
    { make: 'Cisco', model: 'ISR 4331 Router', assetGroup: 'Network Equipment', iso: 'Network', location: locationList[0] || 'Server Room', owner: ownerList[0] || 'IT Department', cost: '3200' },

    // Other document and data (4 assets)
    { make: '', model: 'Customer Database', assetGroup: 'Databases', iso: 'Other document and data (in electronic and other forms)', location: 'Cloud - Azure SQL', owner: ownerList[0] || 'IT Department', cost: '15000' },
    { make: 'Microsoft', model: 'SharePoint Document Library', assetGroup: 'Document Stores', iso: 'Other document and data (in electronic and other forms)', location: 'Cloud - Microsoft 365', owner: ownerList[0] || 'IT Department', cost: '8000' },
    { make: '', model: 'Financial Records Archive', assetGroup: 'Document Stores', iso: 'Other document and data (in electronic and other forms)', location: 'Secure File Room', owner: ownerList[2] || 'Finance', cost: '0' },
    { make: '', model: 'ISO 27001 Documentation', assetGroup: 'Document Stores', iso: 'Other document and data (in electronic and other forms)', location: 'SharePoint', owner: ownerList[3] || 'IT Security', cost: '0' },

    // Software and databases (3 assets)
    { make: 'Microsoft', model: 'SQL Server 2022', assetGroup: 'Databases', iso: 'Software and databases', location: locationList[0] || 'Server Room', owner: ownerList[0] || 'IT Department', cost: '12000' },
    { make: 'Veeam', model: 'Backup & Replication', assetGroup: 'Software', iso: 'Software and databases', location: 'Virtual Infrastructure', owner: ownerList[0] || 'IT Department', cost: '6500' },
    { make: 'SentinelOne', model: 'Endpoint Protection', assetGroup: 'Software', iso: 'Software and databases', location: 'All Endpoints', owner: ownerList[3] || 'IT Security', cost: '9800' },

    // Third party service (2 assets)
    { make: 'Microsoft', model: '365 Business Premium', assetGroup: 'Cloud Services', iso: 'Third party service', location: 'Cloud', owner: ownerList[0] || 'IT Department', cost: '18000' },
    { make: 'Amazon', model: 'AWS Cloud Infrastructure', assetGroup: 'Cloud Services', iso: 'Third party service', location: 'Cloud - AWS', owner: ownerList[0] || 'IT Department', cost: '24000' }
  ];

  const generatedAssets: any[] = [];
  let assetsCreated = 0;
  for (let idx = 0; idx < assetTemplates.length; idx++) {
    const template = assetTemplates[idx];
    const displayName = [template.make, template.model].filter(Boolean).join(' ') || 'Imported asset';
    const asset = {
      id: generateId(),
      referenceNumber: generateRefNumber(),
      make: template.make,
      model: template.model,
      assetGroup: template.assetGroup,
      sku: `SKU-${String(idx + 1).padStart(3, '0')}`,
      description: `${displayName} - ${template.iso}`,
      owner: template.owner,
      assetOwner: template.owner,
      location: template.location,
      status: idx % 5 === 0 ? 'Maintenance' : 'Active',
      quantity: idx % 10 === 0 ? 5 : idx % 5 === 0 ? 3 : 1,
      acquisitionDate: getDate(-365 - (idx * 10)),
      cost: template.cost,
      maintenanceSchedule: idx % 3 === 0 ? 'Quarterly' : 'Annual',
      lastInspectionDate: getDate(-30 - (idx * 5)),
      nextInspectionDate: getDate(30 + (idx * 10)),
      isISO27001: true,
      iso27001Category: template.iso,
      auditDate: getDate(-60 + (idx * 15)),
      auditPeriodicity: '12',
      calibrationDate: idx % 4 === 0 ? getDate(-90) : '',
      calibrationPeriodicity: idx % 4 === 0 ? '12' : '',
      certificationDate: idx % 3 === 0 ? getDate(-120) : '',
      certificationPeriodicity: idx % 3 === 0 ? '12' : '',
      testDate: idx % 2 === 0 ? getDate(-45) : '',
      testPeriodicity: idx % 2 === 0 ? '6' : '',
      datesApplicable: true,
      notes: `Generated sample asset for ${template.iso}`,
      createdAt: getDate(-365),
      updatedAt: getDate(-10)
    };
    generatedAssets.push(asset);
    try {
      await assetActions.addAsset(asset);
      assetsCreated++;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      console.error(`Failed to create asset ${idx + 1} (${displayName}):`, msg);
      errors.push(`Asset "${displayName}": ${msg}`);
    }
  }

  // Risk templates with appropriate controls mapped
  const riskTemplates = [
    // Human Resources risks (8)
    { category: 'Human Resources', asset: 0, threat: 'Unauthorised access by malicious insider', vulnerability: 'Inadequate background screening', control: '6.1 Screening', severity: 'high' },
    { category: 'Human Resources', asset: 0, threat: 'Confidential data disclosure by employee', vulnerability: 'Lack of confidentiality agreements', control: '6.6 Confidentiality or non-disclosure agreements', severity: 'high' },
    { category: 'Human Resources', asset: 1, threat: 'Social engineering attack on staff', vulnerability: 'Insufficient security awareness training', control: '6.3 Information security awareness, education and training', severity: 'medium' },
    { category: 'Human Resources', asset: 2, threat: 'Unauthorised access to training records', vulnerability: 'Weak access controls', control: '5.18 Access rights', severity: 'medium' },
    { category: 'Human Resources', asset: 3, threat: 'Data breach via recruitment portal', vulnerability: 'Insecure application', control: '8.26 Application security requirements', severity: 'high' },
    { category: 'Human Resources', asset: 0, threat: 'Departing employee retaining access', vulnerability: 'No asset return process', control: '5.11 Return of assets', severity: 'medium' },
    { category: 'Human Resources', asset: 1, threat: 'Contractor accessing sensitive data', vulnerability: 'Excessive privilege assignment', control: '8.2 Privileged access rights', severity: 'medium' },
    { category: 'Human Resources', asset: 2, threat: 'Improper disposal of HR documents', vulnerability: 'No secure disposal procedure', control: '7.14 Secure disposal or re-use of equipment', severity: 'low' },
    
    // Infrastructure risks (10)
    { category: 'Infrastructure', asset: 4, threat: 'Unauthorised physical access', vulnerability: 'Weak perimeter security', control: '7.1 Physical security perimeters', severity: 'high' },
    { category: 'Infrastructure', asset: 4, threat: 'Tailgating into secure areas', vulnerability: 'Ineffective entry controls', control: '7.2 Physical entry', severity: 'medium' },
    { category: 'Infrastructure', asset: 5, threat: 'Server room overheating', vulnerability: 'HVAC failure', control: '7.11 Supporting utilities', severity: 'high' },
    { category: 'Infrastructure', asset: 6, threat: 'Power outage affecting IT systems', vulnerability: 'UPS insufficient capacity', control: '7.11 Supporting utilities', severity: 'medium' },
    { category: 'Infrastructure', asset: 7, threat: 'Unauthorised badge cloning', vulnerability: 'Weak access card technology', control: '7.2 Physical entry', severity: 'medium' },
    { category: 'Infrastructure', asset: 8, threat: 'CCTV footage tampering', vulnerability: 'No monitoring of security systems', control: '7.4 Physical security monitoring', severity: 'medium' },
    { category: 'Infrastructure', asset: 4, threat: 'Environmental damage from flooding', vulnerability: 'No flood protection measures', control: '7.5 Protecting against physical and environmental threats', severity: 'low' },
    { category: 'Infrastructure', asset: 7, threat: 'Theft of access control credentials', vulnerability: 'Cards not reported as lost', control: '5.17 Authentication information', severity: 'medium' },
    { category: 'Infrastructure', asset: 5, threat: 'Server room fire', vulnerability: 'Inadequate fire suppression', control: '7.5 Protecting against physical and environmental threats', severity: 'high' },
    { category: 'Infrastructure', asset: 8, threat: 'Blind spots in CCTV coverage', vulnerability: 'Poor camera placement', control: '7.4 Physical security monitoring', severity: 'low' },
    
    // IT and communication equipment risks (16)
    { category: 'IT and communication equipment', asset: 9, threat: 'Server hardware failure', vulnerability: 'No redundancy configuration', control: '8.14 Redundancy of information processing facilities', severity: 'high' },
    { category: 'IT and communication equipment', asset: 10, threat: 'Laptop theft with unencrypted data', vulnerability: 'No full disk encryption', control: '8.24 Use of cryptography', severity: 'high' },
    { category: 'IT and communication equipment', asset: 10, threat: 'Malware infection on laptops', vulnerability: 'Outdated antivirus signatures', control: '8.7 Protection against malware', severity: 'high' },
    { category: 'IT and communication equipment', asset: 11, threat: 'Workstation data leakage', vulnerability: 'No data loss prevention', control: '8.12 Data leakage prevention', severity: 'medium' },
    { category: 'IT and communication equipment', asset: 12, threat: 'Lost mobile device exposing corporate email', vulnerability: 'No remote wipe capability', control: '8.1 User endpoint devices', severity: 'high' },
    { category: 'IT and communication equipment', asset: 13, threat: 'Backup server failure during restore', vulnerability: 'Backups not tested', control: '8.13 Information backup', severity: 'high' },
    { category: 'IT and communication equipment', asset: 14, threat: 'NAS unauthorised access', vulnerability: 'Default admin credentials', control: '8.5 Secure authentication', severity: 'high' },
    { category: 'IT and communication equipment', asset: 9, threat: 'Unpatched server vulnerabilities', vulnerability: 'No patch management process', control: '8.8 Management of technical vulnerabilities', severity: 'high' },
    { category: 'IT and communication equipment', asset: 10, threat: 'Unauthorised USB device usage', vulnerability: 'USB ports not controlled', control: '8.3 Information access restriction', severity: 'medium' },
    { category: 'IT and communication equipment', asset: 15, threat: 'Label printer compromise', vulnerability: 'Printer firmware not updated', control: '8.8 Management of technical vulnerabilities', severity: 'low' },
    { category: 'IT and communication equipment', asset: 11, threat: 'Workstation left unlocked', vulnerability: 'No auto-lock policy', control: '7.7 Clear desk and clear screen', severity: 'medium' },
    { category: 'IT and communication equipment', asset: 16, threat: 'Monitor displaying sensitive data', vulnerability: 'No privacy screens', control: '7.7 Clear desk and clear screen', severity: 'low' },
    { category: 'IT and communication equipment', asset: 10, threat: 'Laptop data not wiped before disposal', vulnerability: 'No secure disposal procedure', control: '7.14 Secure disposal or re-use of equipment', severity: 'medium' },
    { category: 'IT and communication equipment', asset: 12, threat: 'Mobile device connecting to rogue WiFi', vulnerability: 'No VPN enforcement', control: '6.7 Remote working', severity: 'medium' },
    { category: 'IT and communication equipment', asset: 13, threat: 'Backup media lost in transit', vulnerability: 'Unencrypted backup tapes', control: '7.10 Storage media', severity: 'high' },
    { category: 'IT and communication equipment', asset: 9, threat: 'Insufficient server logging', vulnerability: 'Logs not retained or monitored', control: '8.15 Logging', severity: 'medium' },
    
    // Network risks (8)
    { category: 'Network', asset: 17, threat: 'Network switch misconfiguration', vulnerability: 'No configuration management', control: '8.9 Configuration management', severity: 'medium' },
    { category: 'Network', asset: 18, threat: 'Firewall rule bypass', vulnerability: 'Outdated firewall firmware', control: '8.20 Networks security', severity: 'high' },
    { category: 'Network', asset: 19, threat: 'Rogue wireless access point', vulnerability: 'No wireless network monitoring', control: '8.20 Networks security', severity: 'high' },
    { category: 'Network', asset: 20, threat: 'Router DDoS attack', vulnerability: 'No rate limiting configured', control: '8.20 Networks security', severity: 'medium' },
    { category: 'Network', asset: 17, threat: 'VLAN hopping attack', vulnerability: 'No network segregation', control: '8.22 Segregation of networks', severity: 'high' },
    { category: 'Network', asset: 18, threat: 'Firewall configuration drift', vulnerability: 'No change control', control: '8.32 Change management', severity: 'medium' },
    { category: 'Network', asset: 19, threat: 'Guest WiFi accessing corporate network', vulnerability: 'Improper VLAN segregation', control: '8.22 Segregation of networks', severity: 'high' },
    { category: 'Network', asset: 17, threat: 'Network traffic not encrypted', vulnerability: 'No TLS enforcement', control: '8.24 Use of cryptography', severity: 'medium' },
    
    // Data risks (8)
    { category: 'Other document and data (in electronic and other forms)', asset: 21, threat: 'Customer database breach', vulnerability: 'Weak database credentials', control: '8.3 Information access restriction', severity: 'high' },
    { category: 'Other document and data (in electronic and other forms)', asset: 22, threat: 'SharePoint data oversharing', vulnerability: 'Excessive permission grants', control: '5.18 Access rights', severity: 'medium' },
    { category: 'Other document and data (in electronic and other forms)', asset: 23, threat: 'Financial records unauthorised access', vulnerability: 'No document classification', control: '5.12 Classification of information', severity: 'high' },
    { category: 'Other document and data (in electronic and other forms)', asset: 24, threat: 'ISO documentation modification', vulnerability: 'No version control', control: '5.33 Protection of records', severity: 'medium' },
    { category: 'Other document and data (in electronic and other forms)', asset: 21, threat: 'Customer data sent unencrypted', vulnerability: 'No email encryption', control: '5.14 Information transfer', severity: 'high' },
    { category: 'Other document and data (in electronic and other forms)', asset: 22, threat: 'Sensitive documents not labelled', vulnerability: 'No labelling procedures', control: '5.13 Labelling of information', severity: 'medium' },
    { category: 'Other document and data (in electronic and other forms)', asset: 23, threat: 'Financial records retained beyond policy', vulnerability: 'No retention schedule', control: '5.33 Protection of records', severity: 'low' },
    { category: 'Other document and data (in electronic and other forms)', asset: 21, threat: 'Database backup containing PII', vulnerability: 'Production data used in testing', control: '8.11 Data masking', severity: 'high' },
    
    // Software risks (6)
    { category: 'Software and databases', asset: 25, threat: 'SQL Server unpatched vulnerability', vulnerability: 'No vulnerability management', control: '8.8 Management of technical vulnerabilities', severity: 'high' },
    { category: 'Software and databases', asset: 26, threat: 'Backup software configuration error', vulnerability: 'No change testing', control: '8.32 Change management', severity: 'medium' },
    { category: 'Software and databases', asset: 27, threat: 'Antivirus updates failing', vulnerability: 'No monitoring of protection status', control: '8.7 Protection against malware', severity: 'high' },
    { category: 'Software and databases', asset: 25, threat: 'Database SQL injection attack', vulnerability: 'No input validation', control: '8.28 Secure coding', severity: 'high' },
    { category: 'Software and databases', asset: 26, threat: 'Unauthorised software installation', vulnerability: 'No application whitelisting', control: '8.19 Installation of software on operational systems', severity: 'medium' },
    { category: 'Software and databases', asset: 27, threat: 'Endpoint agent tampering', vulnerability: 'No integrity monitoring', control: '8.16 Monitoring activities', severity: 'medium' },
    
    // Third party service risks (4)
    { category: 'Third party service', asset: 28, threat: 'Microsoft 365 account compromise', vulnerability: 'No multi-factor authentication', control: '8.5 Secure authentication', severity: 'high' },
    { category: 'Third party service', asset: 29, threat: 'AWS misconfigured security group', vulnerability: 'No cloud security review', control: '5.23 Information security for use of cloud services', severity: 'high' },
    { category: 'Third party service', asset: 28, threat: 'Third party data processor breach', vulnerability: 'No supplier security assessment', control: '5.19 Information security in supplier relationships', severity: 'high' },
    { category: 'Third party service', asset: 29, threat: 'Cloud service SLA not met', vulnerability: 'No service monitoring', control: '5.22 Monitoring, review and change management of supplier services', severity: 'medium' }
  ];

  const likelihoods = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
  const impacts = ['Insignificant', 'Minor', 'Moderate', 'Serious', 'Catastrophic'];
  const statuses = ['Not Started', 'In Progress', 'Implemented', 'Under Review'];

  let risksCreated = 0;
  for (let idx = 0; idx < riskTemplates.length; idx++) {
    const template = riskTemplates[idx];
    const assetData = generatedAssets[template.asset];
    if (!assetData) {
      errors.push(`InfoSec risk ${idx + 1}: missing asset data at index ${template.asset}`);
      continue;
    }

    let ucLikelihood, ucImpact, managedLikelihood, managedImpact;

    // Set realistic risk levels based on severity
    if (template.severity === 'high') {
      ucLikelihood = likelihoods[3 + Math.floor(Math.random() * 2)]; // Likely or Almost Certain
      ucImpact = impacts[3 + Math.floor(Math.random() * 2)]; // Serious or Catastrophic
      managedLikelihood = likelihoods[1 + Math.floor(Math.random() * 2)]; // Unlikely or Possible
      managedImpact = impacts[1 + Math.floor(Math.random() * 2)]; // Minor or Moderate
    } else if (template.severity === 'medium') {
      ucLikelihood = likelihoods[2 + Math.floor(Math.random() * 2)]; // Possible or Likely
      ucImpact = impacts[2 + Math.floor(Math.random() * 2)]; // Moderate or Serious
      managedLikelihood = likelihoods[0 + Math.floor(Math.random() * 2)]; // Rare or Unlikely
      managedImpact = impacts[1 + Math.floor(Math.random() * 2)]; // Minor or Moderate
    } else {
      ucLikelihood = likelihoods[1 + Math.floor(Math.random() * 2)]; // Unlikely or Possible
      ucImpact = impacts[1 + Math.floor(Math.random() * 2)]; // Minor or Moderate
      managedLikelihood = likelihoods[0]; // Rare
      managedImpact = impacts[0 + Math.floor(Math.random() * 2)]; // Insignificant or Minor
    }

    // Calculate risk scores
    const ucScore = (likelihoods.indexOf(ucLikelihood) + 1) * (impacts.indexOf(ucImpact) + 1);
    const managedScore = (likelihoods.indexOf(managedLikelihood) + 1) * (impacts.indexOf(managedImpact) + 1);

    const ucRiskLevel = ucScore >= 15 ? 'Critical' : ucScore >= 10 ? 'High' : ucScore >= 5 ? 'Medium' : 'Low';
    const managedRiskLevel = managedScore >= 15 ? 'Critical' : managedScore >= 10 ? 'High' : managedScore >= 5 ? 'Medium' : 'Low';

    const risk = {
      id: generateId(),
      referenceNumber: generateRefNumber(),
      riskTitle: `${template.threat} affecting ${assetData.assetGroup || getAssetDisplayName(assetData)}`,
      riskDescription: `Risk of ${template.threat.toLowerCase()} due to ${template.vulnerability.toLowerCase()}`,
      assetCategory: template.category,
      riskCategory: [categories[idx % categories.length]],
      riskOwner: assetData.owner,
      asset: '',
      assetGroup: assetData.assetGroup || '',
      assetGroupCount: assetData.assetGroup ? generatedAssets.filter(a => a.assetGroup === assetData.assetGroup && a.iso27001Category === template.category).length : 0,
      assetLinkType: 'group' as const,
      assetReferenceNumber: '',
      threat: template.threat,
      vulnerability: template.vulnerability,
      ucLikelihood,
      ucImpact,
      ucSeverity: ucImpact,
      ucRiskLevel,
      ucRiskScore: ucScore,
      treatment: treatments[idx % treatments.length],
      treatmentDetail: `Implement ${template.control} control to mitigate this risk`,
      controls: [
        { control: template.control, controlImplementation: getControlImplementation(template.control, idx) },
        ...(idx % 3 === 0 ? [{ control: idx % 6 === 0 ? '5.1 Policies for information security' : '8.1 User endpoint devices', controlImplementation: 'Supporting control implementation' }] : []),
      ],
      control: template.control,
      customControl: '',
      controlStatus: statuses[idx % statuses.length],
      managedLikelihood,
      managedImpact,
      managedSeverity: managedImpact,
      managedRiskLevel,
      managedRiskScore: managedScore,
      opportunities: idx % 5 === 0 ? 'Opportunity to improve security posture and demonstrate compliance' : '',
      actions: 'Monitor and review quarterly',
      comments: idx % 3 === 0 ? 'Priority risk requiring immediate attention' : '',
      dateCompleted: idx % 4 === 0 ? getDate(-30) : '',
      maxReviewPeriod: '12',
      lastDateReviewed: idx % 3 === 0 ? getDate(-60) : '',
      notes: `Sample risk for ${template.category}`,
      createdAt: getDate(-200),
      updatedAt: getDate(-15),
      createdDate: getDate(-200),
      lastModified: getDate(-15),
      controlImplementation: getControlImplementation(template.control, idx),
    };

    try {
      await riskActions.addRisk(risk);
      risksCreated++;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      console.error(`Failed to create InfoSec risk ${idx + 1} (${template.threat}):`, msg);
      errors.push(`InfoSec risk "${template.threat}": ${msg}`);
    }
  }

  // Generate 20 Business Risks
  // Get categories from nonInfoSecRiskStore config
  const businessCategories = nonInfoSecRiskStore.config.riskCategories || [];
  const businessMitigations = nonInfoSecRiskStore.config.mitigations?.map(m => m.name) || [];
  const businessTreatments = nonInfoSecRiskStore.config.treatments?.map(t => t.treatment) || [];
  
  // Fallback if config is empty
  const bizCategories = businessCategories.length > 0 ? businessCategories : [
    'Operational Risk', 'Financial Risk', 'Strategic Risk', 'Compliance Risk',
    'Reputational Risk', 'Health & Safety', 'Environmental Risk', 'Supply Chain Risk'
  ];
  
  const bizMitigations = businessMitigations.length > 0 ? businessMitigations : [
    'Implement risk control', 'Purchase insurance', 'Develop contingency plan',
    'Enhance monitoring', 'Staff training', 'Process improvement',
    'Outsource to specialist', 'Custom mitigation'
  ];

  const bizTreatments = businessTreatments.length > 0 ? businessTreatments : [
    'Treat the risk', 'Avoid the risk', 'Transfer the risk', 'Accept the risk'
  ];

  const businessRiskTemplates = [
    { category: bizCategories[0] || 'Operational Risk', cause: 'Staff shortage', consequence: 'Delayed project delivery', severity: 'high' },
    { category: bizCategories[0] || 'Operational Risk', cause: 'Equipment failure', consequence: 'Production stoppage', severity: 'medium' },
    { category: bizCategories[1] || 'Financial Risk', cause: 'Currency fluctuation', consequence: 'Reduced profit margins', severity: 'medium' },
    { category: bizCategories[1] || 'Financial Risk', cause: 'Customer payment default', consequence: 'Cash flow problems', severity: 'high' },
    { category: bizCategories[2] || 'Strategic Risk', cause: 'Market competition increase', consequence: 'Loss of market share', severity: 'high' },
    { category: bizCategories[2] || 'Strategic Risk', cause: 'Technology obsolescence', consequence: 'Competitive disadvantage', severity: 'medium' },
    { category: bizCategories[3] || 'Compliance Risk', cause: 'Regulatory changes', consequence: 'Non-compliance penalties', severity: 'high' },
    { category: bizCategories[3] || 'Compliance Risk', cause: 'GDPR violation', consequence: 'Regulatory fines', severity: 'high' },
    { category: bizCategories[4] || 'Reputational Risk', cause: 'Negative social media coverage', consequence: 'Brand damage', severity: 'medium' },
    { category: bizCategories[4] || 'Reputational Risk', cause: 'Customer complaint escalation', consequence: 'Public relations crisis', severity: 'medium' },
    { category: bizCategories[5] || 'Health & Safety', cause: 'Workplace accident', consequence: 'Employee injury', severity: 'high' },
    { category: bizCategories[5] || 'Health & Safety', cause: 'Inadequate safety training', consequence: 'HSE investigation', severity: 'medium' },
    { category: bizCategories[6] || 'Environmental Risk', cause: 'Chemical spillage', consequence: 'Environmental contamination', severity: 'high' },
    { category: bizCategories[6] || 'Environmental Risk', cause: 'Excessive waste generation', consequence: 'Regulatory non-compliance', severity: 'low' },
    { category: bizCategories[7] || 'Supply Chain Risk', cause: 'Supplier bankruptcy', consequence: 'Supply interruption', severity: 'high' },
    { category: bizCategories[7] || 'Supply Chain Risk', cause: 'Transportation delay', consequence: 'Customer delivery delay', severity: 'medium' },
    { category: bizCategories[0] || 'Operational Risk', cause: 'IT system outage', consequence: 'Business continuity impact', severity: 'high' },
    { category: bizCategories[1] || 'Financial Risk', cause: 'Interest rate increase', consequence: 'Higher borrowing costs', severity: 'medium' },
    { category: bizCategories[2] || 'Strategic Risk', cause: 'Key staff departure', consequence: 'Loss of expertise', severity: 'medium' },
    { category: bizCategories[3] || 'Compliance Risk', cause: 'Tax law changes', consequence: 'Increased tax liability', severity: 'low' }
  ];

  let businessRisksCreated = 0;
  for (let idx = 0; idx < businessRiskTemplates.length; idx++) {
    const template = businessRiskTemplates[idx];
    const relatedAsset = generatedAssets[idx % generatedAssets.length];

    let ucLikelihood, ucSeverity, managedLikelihood, managedSeverity;

    if (template.severity === 'high') {
      ucLikelihood = likelihoods[3 + Math.floor(Math.random() * 2)];
      ucSeverity = impacts[3 + Math.floor(Math.random() * 2)];
      managedLikelihood = likelihoods[1 + Math.floor(Math.random() * 2)];
      managedSeverity = impacts[1 + Math.floor(Math.random() * 2)];
    } else if (template.severity === 'medium') {
      ucLikelihood = likelihoods[2 + Math.floor(Math.random() * 2)];
      ucSeverity = impacts[2 + Math.floor(Math.random() * 2)];
      managedLikelihood = likelihoods[0 + Math.floor(Math.random() * 2)];
      managedSeverity = impacts[1 + Math.floor(Math.random() * 2)];
    } else {
      ucLikelihood = likelihoods[1 + Math.floor(Math.random() * 2)];
      ucSeverity = impacts[1 + Math.floor(Math.random() * 2)];
      managedLikelihood = likelihoods[0];
      managedSeverity = impacts[0 + Math.floor(Math.random() * 2)];
    }

    const ucScore = (likelihoods.indexOf(ucLikelihood) + 1) * (impacts.indexOf(ucSeverity) + 1);
    const managedScore = (likelihoods.indexOf(managedLikelihood) + 1) * (impacts.indexOf(managedSeverity) + 1);

    const ucRiskLevel = ucScore >= 15 ? 'Critical' : ucScore >= 10 ? 'High' : ucScore >= 5 ? 'Medium' : 'Low';
    const managedRiskLevel = managedScore >= 15 ? 'Critical' : managedScore >= 10 ? 'High' : managedScore >= 5 ? 'Medium' : 'Low';

    const businessRisk = {
      id: generateId(),
      referenceNumber: generateRefNumber(),
      riskTitle: `${template.category}: ${template.cause}`,
      riskDescription: `Risk that ${template.cause.toLowerCase()} could lead to ${template.consequence.toLowerCase()}`,
      riskCategory: [template.category],
      riskOwner: relatedAsset.owner,
      asset: '',
      assetGroup: relatedAsset.assetGroup || '',
      assetGroupCount: relatedAsset.assetGroup ? generatedAssets.filter(a => a.assetGroup === relatedAsset.assetGroup).length : 0,
      assetLinkType: 'group' as const,
      assetReferenceNumber: '',
      assetSku: '',
      cause: template.cause,
      consequence: template.consequence,
      ucLikelihood,
      ucSeverity,
      ucRiskLevel,
      ucRiskScore: ucScore,
      treatment: bizTreatments[idx % bizTreatments.length],
      treatmentDetail: `Apply ${bizMitigations[idx % bizMitigations.length]} strategy`,
      mitigation: bizMitigations[idx % bizMitigations.length],
      mitigationDescription: `Detailed implementation plan for ${bizMitigations[idx % bizMitigations.length]}`,
      customMitigation: idx % 8 === 7 ? 'Custom mitigation approach specific to this risk' : '',
      managedLikelihood,
      managedSeverity,
      managedRiskLevel,
      managedRiskScore: managedScore,
      opportunities: idx % 4 === 0 ? 'Potential for process improvement' : '',
      actions: 'Review quarterly and update mitigation plan as needed',
      comments: idx % 3 === 0 ? 'High priority - requires board attention' : '',
      controlStatus: statuses[idx % statuses.length],
      dateCompleted: idx % 5 === 0 ? getDate(-45) : '',
      maxReviewPeriod: '6',
      lastDateReviewed: idx % 2 === 0 ? getDate(-30) : '',
      notes: `Sample business risk for ${template.category}`,
      createdAt: getDate(-180),
      updatedAt: getDate(-20),
      createdDate: getDate(-180),
      lastModified: getDate(-20)
    };

    try {
      await nonInfoSecRiskActions.addRisk(businessRisk);
      businessRisksCreated++;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      console.error(`Failed to create business risk ${idx + 1} (${template.cause}):`, msg);
      errors.push(`Business risk "${template.cause}": ${msg}`);
    }
  }

  return {
    assetsGenerated: assetsCreated,
    risksGenerated: risksCreated,
    businessRisksGenerated: businessRisksCreated,
    errors,
  };
};

// Helper function to generate realistic control implementations
function getControlImplementation(control: string, index: number): string {
  const implementations: Record<string, string[]> = {
    '6.1 Screening': [
      'All employees undergo DBS checks before employment. References verified with previous employers.',
      'Comprehensive background screening including criminal record checks, identity verification, and employment history validation.'
    ],
    '6.3 Information security awareness, education and training': [
      'Annual mandatory security awareness training for all staff. Monthly phishing simulation exercises.',
      'Quarterly security workshops and monthly security newsletters distributed to all employees.'
    ],
    '7.1 Physical security perimeters': [
      'Building surrounded by 2.4m security fencing with CCTV coverage. Single controlled entry point during business hours.',
      'Perimeter alarm system active outside business hours. Regular security patrols of premises.'
    ],
    '7.2 Physical entry': [
      'HID proximity card readers installed at all entry points. Visitor management system with sign-in required.',
      'Biometric readers for server room access. All access attempts logged and monitored.'
    ],
    '8.1 User endpoint devices': [
      'All devices encrypted with BitLocker. Mobile Device Management (MDM) enforces security policies.',
      'Endpoint devices protected with SentinelOne. Remote wipe capability enabled for all mobile devices.'
    ],
    '8.7 Protection against malware': [
      'SentinelOne deployed on all endpoints with real-time protection. Daily signature updates automated.',
      'Anti-malware protection with behaviour-based detection. Quarantine and remediation automated.'
    ],
    '8.24 Use of cryptography': [
      'TLS 1.3 enforced for all data in transit. AES-256 encryption for data at rest.',
      'Full disk encryption on all laptops and mobile devices. Encrypted email gateway for sensitive communications.'
    ]
  };

  const controlKey = control.split(' ').slice(0, 2).join(' '); // e.g., "8.24 Use"
  const options = implementations[controlKey] || [
    `Implementation of ${control} through documented procedures and technical controls.`,
    `${control} implemented with regular monitoring and review processes.`
  ];

  return options[index % options.length];
}

// Helper function to generate justifications
function getJustification(control: string, threat: string): string {
  return `${control} directly addresses the risk of ${threat.toLowerCase()}. This control is essential for maintaining our security posture and ensuring regulatory compliance.`;
}

// Clear all data function
export const clearAllData = async () => {
  // Delete from database
  await Promise.all([
    api.del('/assets'),
    api.del('/risks'),
    api.del('/non-infosec-risks'),
  ]);

  // Clear local stores
  assetStore.assets = [];
  riskStore.risks = [];
  nonInfoSecRiskStore.risks = [];

  // Reset filters
  riskStore.searchTerm = '';
  riskStore.categoryFilter = 'all';
  riskStore.statusFilter = 'all';
  nonInfoSecRiskStore.searchTerm = '';
  nonInfoSecRiskStore.categoryFilter = 'all';
  nonInfoSecRiskStore.statusFilter = 'all';
  nonInfoSecRiskStore.treatmentFilter = 'all';

  // Reload configuration stores so they don't appear reset
  await riskMatrixActions.loadConfig();

  console.log('All data cleared from database and stores');
};
